/**
 * Governance Hub list row — one obligation, one line.
 *
 * The row answers "what and when" at a glance: due date, title, where it
 * comes from, who owns it, where it stands. Everything deeper — the contract
 * language, the thread, the controls — lives in the drawer a click opens.
 * That split (scan the list, act in the drawer) is what makes thirty dated
 * obligations readable, and it is the pattern the card grid was fighting:
 * when every card says everything, the reader gets nothing.
 */
import React from 'react';

const SOURCE_STYLES = {
  memo: 'bg-violet-100 text-violet-700 border-violet-200',
  cap: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  calendar: 'bg-sky-100 text-sky-700 border-sky-200',
};

const STATUS_STYLES = {
  overdue: 'bg-red-100 text-red-700 border-red-200',
  due_soon: 'bg-amber-100 text-amber-800 border-amber-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  no_action_needed: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-sky-100 text-sky-700 border-sky-200',
  upcoming: 'bg-blue-50 text-blue-700 border-blue-200',
};

const statusLabel = (s) => ({
  overdue: 'Overdue', due_soon: 'Due soon', completed: 'Completed',
  no_action_needed: 'No action needed', in_progress: 'In progress', upcoming: 'Upcoming',
}[(s || '').toLowerCase()] || 'Upcoming');

const sourceLabel = (s, item) => {
  const n = (s || '').toLowerCase();
  if (n === 'memo') return 'Memo';
  if (n === 'cap') return 'CAP';
  const program = item?.obligation_type || item?.type || '';
  if (program === 'State Medicaid (SMAC)') return 'Medicaid';
  if (program === 'CMS') return 'Medicare';
  return program || 'Calendar';
};

export default function ItemRow({ item, effectiveStatus, onSelect, calendarStatusLabel }) {
  const source = item.source || 'calendar';
  const isContract = !!item.smac_obligation_id;

  // The due date, split for the date rail.
  const rawDate = item.due_date || item.end_date || item.start_date;
  const d = rawDate ? new Date(rawDate) : null;
  const dateValid = d && !Number.isNaN(d.getTime());
  const month = dateValid ? d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }) : '';
  const day = dateValid ? d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' }) : '—';
  const year = dateValid ? d.toLocaleDateString('en-US', { year: 'numeric', timeZone: 'UTC' }) : '';

  const isOverdue = effectiveStatus === 'overdue';
  const isDueSoon = effectiveStatus === 'due_soon';
  const isDone = effectiveStatus === 'completed' || effectiveStatus === 'no_action_needed';

  // Where this comes from and who carries it — one quiet line under the title.
  const origin = isContract
    ? [item.contract_name, item.contract_section].filter(Boolean).join(' · ')
    : (source === 'memo' ? (Array.isArray(item.departments) && item.departments.length ? item.departments.join(', ') : null) : null);
  const owner = item.contract_owner_name || item.assignee_name || item.owner_name || null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect?.(item)}
      className={`group flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors hover:bg-slate-50 focus:outline-none focus:bg-indigo-50/50 ${isDone ? 'opacity-60' : ''}`}
    >
      {/* Date rail — urgency is color, position is constant. */}
      <div className={`w-14 flex-shrink-0 text-center leading-tight ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-600' : 'text-slate-500'}`}>
        <div className="text-[10px] font-semibold uppercase tracking-wide">{month || ' '}</div>
        <div className="text-xl font-bold">{day}</div>
        <div className="text-[10px]">{year}</div>
      </div>

      {/* What, and where it comes from. */}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold truncate ${isDone ? 'text-slate-500' : 'text-slate-800'}`}>
          {(item.title && String(item.title).trim()) || '—'}
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">
          {[origin, owner].filter(Boolean).join('  ·  ') || ' '}
        </div>
      </div>

      {/* Where it stands. */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {Number(item.deliverable_count) > 0 && (
          <span className="inline-flex items-center gap-0.5 text-xs text-slate-500" title={`${item.deliverable_count} deliverable${Number(item.deliverable_count) === 1 ? '' : 's'} attached`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
            {item.deliverable_count}
          </span>
        )}
        <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-lg text-[10px] font-semibold uppercase border ${SOURCE_STYLES[source] || SOURCE_STYLES.calendar}`}>
          {sourceLabel(source, item)}
        </span>
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${STATUS_STYLES[effectiveStatus] || STATUS_STYLES.upcoming}`}>
          {source === 'cap' ? statusLabel(effectiveStatus) : calendarStatusLabel?.(item) ?? statusLabel(effectiveStatus)}
        </span>
        <svg className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}
